// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import { Help } from '../../lib/help';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  CropMaxHoldTimeDataQuery,
  CropMaxHoldTimeDataQueryVariables,
  EditCropMaxHoldTimeMutation,
  EditCropMaxHoldTimeMutationVariables,
} from '../../types/__generated__/graphql';
import {
  getDaysFromSeconds,
  getNumericID,
  getSecondsFromDaysString,
  getTimeRanges,
  validateNumInput,
} from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_MAX_HOLD_TIME_MUTATION = gql`
  mutation EditCropMaxHoldTime(
    $crop_id: Int!
    $max_hold_time: Int!
  ) {
    editCropHoldTime(
      input: {
        crop_id: $crop_id
        max_hold_time: $max_hold_time
      }
    ) {
      updated_crop {
        id
        max_hold_time
        updated_at
      }
    }
  }
`;

const CROP_MAX_HOLD_TIME_QUERY = gql`
  query CropMaxHoldTimeData($id: ID!) {
    crop(id: $id) {
      id
      max_hold_time
      harvest_buffer_time
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropMaxHoldTimeDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const { timezone } = useUserContext();
  const { todayStart } = getTimeRanges(timezone);
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    CropMaxHoldTimeDataQuery,
    CropMaxHoldTimeDataQueryVariables
  >(CROP_MAX_HOLD_TIME_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [editCropMaxHoldTime, { loading: editCropLoading }] = useMutation<
    EditCropMaxHoldTimeMutation,
    EditCropMaxHoldTimeMutationVariables
  >(EDIT_CROP_MAX_HOLD_TIME_MUTATION);

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const maxHoldTime = crop.max_hold_time != null ? getDaysFromSeconds(crop.max_hold_time).toString() : '';
            setMaxHoldTime(maxHoldTime);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, fetchCrop]);

  const disableFields = cropDataLoading || editCropLoading;

  const [maxHoldTime, setMaxHoldTime] = React.useState('');
  const [maxHoldTimeValidation, setMaxHoldTimeValidation] = React.useState('');

  const harvestBufferTime =
    data?.crop?.harvest_buffer_time != null ? getDaysFromSeconds(data.crop.harvest_buffer_time) : undefined;
  const existingMaxHoldTime = data?.crop?.max_hold_time != null ? getDaysFromSeconds(data.crop.max_hold_time) : 0;

  const onChangeMaxHoldTime = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMaxHoldTime(e.target.value);
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const maxHoldTimeAsNum = Number.parseFloat(e.target.value) || Number.POSITIVE_INFINITY;
    if (harvestBufferTime != null && harvestBufferTime > maxHoldTimeAsNum) {
      setMaxHoldTimeValidation('Shelf life must be greater than or equal to harvest buffer time');
    } else {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMaxHoldTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const onMaxHoldTimeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const maxHoldTimeAsNum = Number.parseFloat(e.target.value) || Number.POSITIVE_INFINITY;
    if (harvestBufferTime != null && harvestBufferTime > maxHoldTimeAsNum) {
      setMaxHoldTimeValidation('Shelf life must be greater than or equal to harvest buffer time');
    } else {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMaxHoldTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editCropMaxHoldTime({
      refetchQueries: [
        {
          query: SURPLUSES_BUFFERS_QUERY,
          variables: { timeAfter: todayStart },
        },
      ],
      variables: {
        max_hold_time: getSecondsFromDaysString(maxHoldTime, true) ?? 0,
        crop_id: getNumericID(cropId),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropHoldTime != null) {
          enqueueSnackbar('Updated crop shelf life', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setMaxHoldTime('');
        setMaxHoldTimeValidation('');
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = maxHoldTimeValidation !== '';
  const requiredFieldsFilled: boolean = maxHoldTime !== '';
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={1}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="shelf-life"
          id="shelf-life"
          label="Shelf Life (days)"
          value={maxHoldTime}
          onChange={onChangeMaxHoldTime}
          onBlur={onMaxHoldTimeBlur}
          onEnter={onEnter}
          subtext={maxHoldTimeValidation}
          error={maxHoldTimeValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        <Help tooltip="This is how long you can hold this crop after harvest before it can no longer be sold" />
      </Box>
      {(existingMaxHoldTime !== undefined || harvestBufferTime !== undefined) && (
        <Box>
          {existingMaxHoldTime !== undefined && (
            <Typography variant="body1">Existing Shelf Life: {existingMaxHoldTime} days</Typography>
          )}
          {harvestBufferTime !== undefined && (
            <Typography variant="body1">Harvest Buffer Time: {harvestBufferTime} days</Typography>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Crop Shelf Life', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
